import { diagramIsEmpty, type Drill, type LibraryDrill, type PlanItem } from '@hoopify/shared';
import * as P from './plan';

export type DrillOrigin = 'mine' | 'shared' | 'hoopify';

export interface DrillMatch {
  key: string;
  title: string;
  minutes: number;
  origin: DrillOrigin;
  /** Who shared it (shared drills only). */
  from: string | null;
  hasDiagram: boolean;
  /** True when the name matched; false when only its skills or notes did. */
  byName: boolean;
  make: () => PlanItem;
}

const ORIGIN_ORDER: Record<DrillOrigin, number> = { mine: 0, shared: 1, hoopify: 2 };

const norm = (s: string) => s.toLowerCase().replace(/[’']/g, '').replace(/\s+/g, ' ').trim();

/**
 * How well a drill matches, lower is better; null for no match.
 *  0 the name starts with the search, 1 a word in the name does,
 *  2 the name contains it (or every word of it), 3 a skill or category does,
 *  4 the description or coaching points do.
 */
export function matchScore(query: string, title: string, tags: string[], text: string): number | null {
  const q = norm(query);
  if (!q) return null;
  const words = q.split(' ');
  const t = norm(title);
  if (t.startsWith(q)) return 0;
  if (` ${t}`.includes(` ${q}`)) return 1;
  if (t.includes(q) || words.every((w) => t.includes(w))) return 2;
  const tagText = norm(tags.join(' '));
  if (words.every((w) => tagText.includes(w) || t.includes(w))) return 3;
  const all = `${t} ${tagText} ${norm(text)}`;
  if (words.every((w) => all.includes(w))) return 4;
  return null;
}

/**
 * Searches your drills, drills shared with you and the Hoopify library together.
 * Name matches come first, then yours before shared before Hoopify, then the closest match.
 * A Hoopify drill you've already saved under the same name is left out.
 */
export function searchDrills(
  query: string,
  sources: { mine?: Drill[]; shared?: Drill[]; hoopify?: LibraryDrill[] },
  limit = 8,
): DrillMatch[] {
  const found: (DrillMatch & { score: number })[] = [];
  const seen = new Set<string>();
  const add = (m: DrillMatch, score: number | null) => {
    if (score === null) return;
    const name = norm(m.title);
    if (m.origin === 'hoopify' && seen.has(name)) return;
    seen.add(name);
    found.push({ ...m, score });
  };
  for (const [origin, list] of [['mine', sources.mine], ['shared', sources.shared]] as const) {
    for (const d of list ?? []) {
      const score = matchScore(query, d.title, d.tags, `${d.description ?? ''} ${d.coachingPoints ?? ''}`);
      add(
        {
          key: `${origin}:${d.id}`,
          title: d.title,
          minutes: d.minutes,
          origin,
          from: origin === 'shared' ? d.owner.name : null,
          hasDiagram: !diagramIsEmpty(d.diagram),
          byName: score !== null && score <= 2,
          make: () => P.itemFromDrill(d),
        },
        score,
      );
    }
  }
  for (const d of sources.hoopify ?? []) {
    const score = matchScore(query, d.title, [...d.tags, d.category], `${d.summary} ${d.description} ${d.coachingPoints}`);
    add(
      {
        key: `hoopify:${d.id}`,
        title: d.title,
        minutes: d.minutes,
        origin: 'hoopify',
        from: null,
        hasDiagram: !!d.diagram,
        byName: score !== null && score <= 2,
        make: () => P.itemFromLibrary(d),
      },
      score,
    );
  }
  return found
    .sort(
      (a, b) =>
        Number(!a.byName) - Number(!b.byName) ||
        ORIGIN_ORDER[a.origin] - ORIGIN_ORDER[b.origin] ||
        a.score - b.score ||
        a.title.localeCompare(b.title),
    )
    .slice(0, limit)
    .map(({ score: _, ...m }) => m);
}
